import { useCallback, useEffect, useRef, type RefObject } from 'react';

const useIframeEnterKey = (
  iframeRef: RefObject<HTMLIFrameElement | null>,
  onEnter: () => void
) => {
  const onEnterRef = useRef(onEnter);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    onEnterRef.current = onEnter;
  }, [onEnter]);

  useEffect(() => () => cleanupRef.current?.(), []);

  return useCallback(() => {
    cleanupRef.current?.();

    const iframeDocument = iframeRef.current?.contentDocument;
    const parentDocument = iframeRef.current?.ownerDocument;
    const iframeWindow = iframeRef.current?.contentWindow;
    const parentWindow = parentDocument?.defaultView;
    if (!iframeDocument || !parentDocument || !iframeWindow || !parentWindow) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.isComposing || event.repeat) return;

      event.preventDefault();

      for (const document of [parentDocument, iframeDocument]) {
        const nextButtons = [
          document.querySelector('#trQnOK [alt="Next" i]'),
          document.getElementById('imgNextQuestion'),
        ];

        for (const nextButton of nextButtons) {
          if (!nextButton || !nextButton.isConnected) continue;

          const element = nextButton as HTMLElement;
          const styles = document.defaultView?.getComputedStyle(element);
          if (element.hidden || styles?.display === 'none' || styles?.visibility === 'hidden' || element.getClientRects().length === 0) continue;

          element.click();
          return;
        }
      }

      onEnterRef.current();
    };

    parentWindow.addEventListener('keydown', handleKeyDown, true);
    iframeWindow.addEventListener('keydown', handleKeyDown, true);
    cleanupRef.current = () => {
      parentWindow.removeEventListener('keydown', handleKeyDown, true);
      iframeWindow.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [iframeRef]);
};

export default useIframeEnterKey;
